import Typography from '@mui/material/Typography'
import clsx from 'clsx'
import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { TEST_IDS } from 'src/constants/testIds'
import logToPostHog from 'src/helpers/logToPosthog'
import { CALENDAR_DIRECTIONS } from '@customTypes/AppointmentCalendarDirections'
import { useAppointmentCalendar } from '@context/AppointmentCalendarContext/AppointmentCalendarContext'
import { useChoiceState } from '@context/ChoiceContext'
import { useConfig } from '@context/ConfigurationContext'
import { useSelectTimeslot } from '@hooks/useSelectTimeslot'
import { useClinicOfficialName } from '@hooks/useClinicOfficialName'
import { shortenTimeString } from '@screens/Appointment/helpers/shortenTimeString'
import { Availabilities, Availability } from '@customTypes/Availabilities'
import useStyles, { SlotsUseStylesProps } from './Slots.styles'

type SlotsProps = SlotsUseStylesProps & {
  clinicId: string
  currentWeek: Availabilities[]
  activeSlotRef: React.MutableRefObject<HTMLDivElement | null>
  isExpandable: boolean
}

export const Slots = ({
  clinicId,
  currentWeek,
  activeSlotRef,
  isExpandable,
  variant = 'default',
}: SlotsProps): ReactElement => {
  const classes = useStyles({ variant })
  const selectTimeslot = useSelectTimeslot()
  const { t } = useTranslation()
  const { selectedBookableDateAndTime } = useChoiceState()
  const { clinicExpandedState, toggleClinicExpanded, isTransitioning, transition } = useAppointmentCalendar()
  const isExpanded = clinicExpandedState[clinicId]
  const isCollapsed = isExpandable && !isExpanded
  const { clinicId: baseClinicId } = useConfig()
  const clinicName = useClinicOfficialName(clinicId)
  const baseClinicName = useClinicOfficialName(baseClinicId)

  const onSelectTimeSlot = (availability: Availability) => {
    selectTimeslot(availability, clinicId)

    if (clinicId !== baseClinicId)
      logToPostHog('Nearby timeslot selected', { nearbyClinic: clinicName, baseClinic: baseClinicName })
  }

  return (
    <>
      <div
        className={clsx(
          {
            [classes.isTransitioning]: isTransitioning,
            [classes.transitionRight]: transition === CALENDAR_DIRECTIONS.RIGHT,
            [classes.transitionLeft]: transition === CALENDAR_DIRECTIONS.LEFT,
            [classes.isTransitioningToLeft]: isTransitioning && transition === CALENDAR_DIRECTIONS.RIGHT,
            [classes.isTransitioningToRight]: isTransitioning && transition === CALENDAR_DIRECTIONS.LEFT,
          },
          classes.root
        )}
      >
        {currentWeek.map((day) => {
          const dayAvailabilities = isCollapsed ? day.availabilities.slice(0, 2) : day.availabilities

          return (
            <div key={day.date} className={classes.dayColumn}>
              <div data-test={day.date} className={classes.slots}>
                {dayAvailabilities.map((_, index) => {
                  const availability = dayAvailabilities[index]
                  if (availability) {
                    const isSelected =
                      selectedBookableDateAndTime?.date === day.date &&
                      selectedBookableDateAndTime?.time === shortenTimeString(availability.start) &&
                      selectedBookableDateAndTime?.clinicId === clinicId

                    return (
                      <div
                        key={availability.start}
                        data-test={`${availability ? 'populated-' : 'empty-'}appointment-slot`}
                        className={clsx(classes.slot, {
                          [classes.selected]: isSelected,
                          [classes.empty]: !availability,
                        })}
                        onClick={() => onSelectTimeSlot(availability)}
                        ref={isSelected ? activeSlotRef : undefined}
                      >
                        <Typography variant="Lato Emphasized 2">{availability.start.substring(0, 5)}</Typography>
                      </div>
                    )
                  }
                  return <div key={index} className={classes.empty} />
                })}
              </div>
            </div>
          )
        })}
      </div>
      {isExpandable && !isTransitioning && (
        <div
          onClick={() => toggleClinicExpanded(clinicId)}
          className={classes.toggleMoreAppointments}
          data-test={`${TEST_IDS.toggleMoreAppointments}-${clinicId}`}
        >
          {isExpanded ? t('show_less_timeslots') : t('show_more_timeslots')}
        </div>
      )}
    </>
  )
}
